/**
 * L2 Structural Audit — deterministic SQL pass.
 *
 * Wykrywa problemy grafu mapy bez LLM:
 *  - Orphans (brak relacji wchodzących/wychodzących)
 *  - No-incoming spokes (tylko wychodzące linki)
 *  - Duplicate groups (entity+intent z Jaccard slug tokens)
 *  - Seed imbalance (CV per-seed node count)
 *  - Cross-seed bridges count
 *  - Degree distribution
 *
 * Reguse: deduplicationService.jaccardSimilarity + tokenize.
 */

import { supabase } from "@/integrations/supabase/client";
import { jaccardSimilarity, tokenize } from "@/services/deduplicationService";
import type { AuditConfig, AuditSeverity, AuditIssue } from "@/types/thematicMaps";

const PREFIX = "[AUDIT-L2-STRUCT]";

export interface L2StructuralResult {
  score: number;
  max: number;
  verdict: "pass" | "warn" | "fail";
  orphans: { count: number; ratio: number; severity: AuditSeverity; affected: string[] };
  no_incoming_spokes: { count: number; ratio: number; severity: AuditSeverity; affected: string[] };
  duplicate_groups: Array<{
    entity: string;
    intent: string;
    count: number;
    jaccard: number;
    severity: AuditSeverity;
    affected_slugs: string[];
  }>;
  seed_imbalance: { cv: number; severity: AuditSeverity; min_count: number; max_count: number };
  seeds_without_nodes: string[];
  cross_seed_bridges: number;
  intra_seed_relations: number;
  degree_distribution: Array<{ incoming: number; count: number }>;
  issues: AuditIssue[];
}

interface NodeRow {
  id: string;
  slug: string;
  title: string | null;
  seed_id: string;
  node_role: string | null;
  primary_entity_raw: string | null;
  primary_intent: string | null;
}

interface RelationRow {
  from_node_id: string;
  to_node_id: string;
  relation_type: string;
  weight: number | null;
}

const DEFAULT_CONFIG: Required<Pick<
  AuditConfig,
  "orphan_ratio" | "duplicate_count" | "duplicate_jaccard" | "seed_imbalance_cv" | "max_affected_per_issue"
>> = {
  orphan_ratio: { error: 50, warning: 30 },
  duplicate_count: { error: 3, warning: 2 },
  duplicate_jaccard: { error: 0.5, warning: 0.3 },
  seed_imbalance_cv: { error: 1.0, warning: 0.7 },
  max_affected_per_issue: 10,
};

function mergeConfig(partial?: AuditConfig): typeof DEFAULT_CONFIG {
  return {
    orphan_ratio: partial?.orphan_ratio ?? DEFAULT_CONFIG.orphan_ratio,
    duplicate_count: partial?.duplicate_count ?? DEFAULT_CONFIG.duplicate_count,
    duplicate_jaccard: partial?.duplicate_jaccard ?? DEFAULT_CONFIG.duplicate_jaccard,
    seed_imbalance_cv: partial?.seed_imbalance_cv ?? DEFAULT_CONFIG.seed_imbalance_cv,
    max_affected_per_issue: partial?.max_affected_per_issue ?? DEFAULT_CONFIG.max_affected_per_issue,
  };
}

function severity(
  value: number,
  thresholds: { error: number; warning: number },
): AuditSeverity {
  if (value >= thresholds.error) return "error";
  if (value >= thresholds.warning) return "warning";
  return "ok";
}

export async function auditStructural(
  mapId: string,
  config?: AuditConfig,
): Promise<L2StructuralResult> {
  const cfg = mergeConfig(config);

  // Load nodes + relations
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: nodesData } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, slug, title, seed_id, node_role, primary_entity_raw, primary_intent")
    .eq("map_id", mapId);
  const nodes = (nodesData || []) as NodeRow[];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: relData } = await (supabase as any)
    .from("thematic_node_relations")
    .select("from_node_id, to_node_id, relation_type, weight")
    .eq("map_id", mapId);
  const relations = (relData || []) as RelationRow[];

  // Seeds (dla imbalance + seeds_without_nodes)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: seedsData } = await (supabase as any)
    .from("thematic_seeds")
    .select("id, name")
    .eq("map_id", mapId);
  const seeds = (seedsData || []) as Array<{ id: string; name: string }>;

  const total = nodes.length;
  const issues: AuditIssue[] = [];

  // ─── Degree distribution ───
  const inDeg = new Map<string, number>();
  const outDeg = new Map<string, number>();
  for (const n of nodes) {
    inDeg.set(n.id, 0);
    outDeg.set(n.id, 0);
  }
  for (const r of relations) {
    outDeg.set(r.from_node_id, (outDeg.get(r.from_node_id) ?? 0) + 1);
    inDeg.set(r.to_node_id, (inDeg.get(r.to_node_id) ?? 0) + 1);
  }

  const degreeDistMap = new Map<number, number>();
  for (const n of nodes) {
    const d = inDeg.get(n.id) ?? 0;
    degreeDistMap.set(d, (degreeDistMap.get(d) ?? 0) + 1);
  }
  const degree_distribution = [...degreeDistMap.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([incoming, count]) => ({ incoming, count }));

  // ─── Orphans (fully isolated) ───
  const orphans = nodes.filter((n) => (inDeg.get(n.id) ?? 0) === 0 && (outDeg.get(n.id) ?? 0) === 0);
  const orphanRatio = total > 0 ? (orphans.length / total) * 100 : 0;
  const orphanSev = severity(orphanRatio, cfg.orphan_ratio);

  if (orphans.length > 0) {
    issues.push({
      severity: orphanSev,
      dimension: "topology",
      message: `${orphans.length} nodów fully isolated (brak relacji) — ${orphanRatio.toFixed(1)}%`,
      affected_slugs: orphans.slice(0, cfg.max_affected_per_issue).map((n) => n.slug),
      suggested_fix: "Dodaj relacje (sibling/next_step/prerequisite) lub usuń te nody",
    });
  }

  // ─── No incoming (weaker than orphan) ───
  const noIncoming = nodes.filter(
    (n) => (inDeg.get(n.id) ?? 0) === 0 && (outDeg.get(n.id) ?? 0) > 0,
  );
  const noIncomingRatio = total > 0 ? (noIncoming.length / total) * 100 : 0;
  const noIncomingSev = severity(noIncomingRatio, cfg.orphan_ratio);

  // ─── Duplicate groups (entity + intent + Jaccard slug tokens) ───
  const groups = new Map<string, NodeRow[]>();
  for (const n of nodes) {
    const entity = (n.primary_entity_raw || "").trim().toLowerCase();
    const intent = (n.primary_intent || "").trim().toLowerCase();
    if (!entity || !intent) continue;
    const key = `${entity}|${intent}`;
    const list = groups.get(key) || [];
    list.push(n);
    groups.set(key, list);
  }

  const duplicate_groups: L2StructuralResult["duplicate_groups"] = [];
  for (const [key, list] of groups.entries()) {
    if (list.length < 2) continue;
    const [entity, intent] = key.split("|");
    // Jaccard slug tokens — pre-process: zamień myślniki na spacje (tokenize
    // usuwa znaki niealfanumeryczne bez zamiany, więc slug "jak-zwijac-sushi"
    // stałby się "jakzwijacsushi" = 1 token). Split po myślnikach daje tokeny.
    const slugs = list.map((n) => tokenize(n.slug.replace(/-/g, " ")));
    let simSum = 0;
    let pairs = 0;
    for (let i = 0; i < slugs.length; i++) {
      for (let j = i + 1; j < slugs.length; j++) {
        simSum += jaccardSimilarity(slugs[i], slugs[j]);
        pairs++;
      }
    }
    const avgJaccard = pairs > 0 ? simSum / pairs : 0;

    let sev: AuditSeverity = "ok";
    if (list.length >= cfg.duplicate_count.error && avgJaccard >= cfg.duplicate_jaccard.error) {
      sev = "error";
    } else if (list.length >= cfg.duplicate_count.warning && avgJaccard >= cfg.duplicate_jaccard.warning) {
      sev = "warning";
    } else if (list.length >= cfg.duplicate_count.warning) {
      sev = "info" as AuditSeverity;
    }

    if (sev !== "ok") {
      const affectedSlugs = list.slice(0, cfg.max_affected_per_issue).map((n) => n.slug);
      duplicate_groups.push({
        entity,
        intent,
        count: list.length,
        jaccard: Math.round(avgJaccard * 100) / 100,
        severity: sev,
        affected_slugs: affectedSlugs,
      });
      if (sev === "error" || sev === "warning") {
        issues.push({
          severity: sev,
          dimension: "duplicates",
          message: `${list.length} duplikatów entity="${entity}" intent="${intent}" (Jaccard=${(avgJaccard * 100).toFixed(0)}%)`,
          affected_slugs: affectedSlugs,
          suggested_fix: sev === "error"
            ? `Zmerguj ${list.length} node'ów — prawdopodobnie to ten sam content`
            : `Sprawdź czy ${list.length} node'ów to różne kąty czy duplikat`,
        });
      }
    }
  }
  duplicate_groups.sort((a, b) => b.count - a.count || b.jaccard - a.jaccard);

  // ─── Seed imbalance (CV) ───
  const seedCounts = new Map<string, number>();
  for (const n of nodes) seedCounts.set(n.seed_id, (seedCounts.get(n.seed_id) ?? 0) + 1);
  const counts = seeds.map((s) => seedCounts.get(s.id) ?? 0);
  const seedsWithoutNodes = seeds.filter((s) => (seedCounts.get(s.id) ?? 0) === 0);

  let cv = 0;
  let minCount = 0;
  let maxCount = 0;
  if (counts.length > 0) {
    const mean = counts.reduce((a, b) => a + b, 0) / counts.length;
    const variance = counts.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / counts.length;
    const sd = Math.sqrt(variance);
    cv = mean > 0 ? sd / mean : 0;
    minCount = Math.min(...counts);
    maxCount = Math.max(...counts);
  }
  const imbalanceSev = severity(cv, cfg.seed_imbalance_cv);

  if (seedsWithoutNodes.length > 0) {
    issues.push({
      severity: "warning",
      dimension: "structure",
      message: `${seedsWithoutNodes.length} seedów bez nodów: ${seedsWithoutNodes.map((s) => s.name).join(", ")}`,
      affected_slugs: [],
      suggested_fix: "Wygeneruj nody dla tych seedów lub usuń seedy z mapy",
    });
  }
  if (imbalanceSev !== "ok" && counts.length > 1) {
    issues.push({
      severity: imbalanceSev,
      dimension: "structure",
      message: `Seed imbalance CV=${cv.toFixed(2)} — min=${minCount} nodów, max=${maxCount} nodów per seed`,
      affected_slugs: [],
      suggested_fix: "Rozważ rebalans: rozdziel seed z max nodów lub dogeneruj dla seed z min",
    });
  }

  // ─── Cross-seed vs intra-seed bridges ───
  const nodeSeedMap = new Map<string, string>();
  for (const n of nodes) nodeSeedMap.set(n.id, n.seed_id);
  let crossSeed = 0;
  let intraSeed = 0;
  for (const r of relations) {
    const s1 = nodeSeedMap.get(r.from_node_id);
    const s2 = nodeSeedMap.get(r.to_node_id);
    if (s1 && s2 && s1 === s2) intraSeed++;
    else if (s1 && s2) crossSeed++;
  }

  // ─── Score (0-20) ───
  // -3 za orphans ratio, -3 za no_incoming, -4 za duplikaty, -3 za imbalance, -2 za seeds_without_nodes
  let score = 20;
  if (orphanSev === "error") score -= 6;
  else if (orphanSev === "warning") score -= 3;
  if (noIncomingSev === "error") score -= 3;
  else if (noIncomingSev === "warning") score -= 2;
  score -= duplicate_groups.filter((d) => d.severity === "error").length * 2;
  score -= duplicate_groups.filter((d) => d.severity === "warning").length * 1;
  if (imbalanceSev === "error") score -= 3;
  else if (imbalanceSev === "warning") score -= 1;
  score -= Math.min(seedsWithoutNodes.length, 3);
  score = Math.max(0, score);

  const verdict: "pass" | "warn" | "fail" = score >= 16 ? "pass" : score >= 10 ? "warn" : "fail";

  return {
    score,
    max: 20,
    verdict,
    orphans: {
      count: orphans.length,
      ratio: Math.round(orphanRatio * 10) / 10,
      severity: orphanSev,
      affected: orphans.slice(0, cfg.max_affected_per_issue).map((n) => n.slug),
    },
    no_incoming_spokes: {
      count: noIncoming.length,
      ratio: Math.round(noIncomingRatio * 10) / 10,
      severity: noIncomingSev,
      affected: noIncoming.slice(0, cfg.max_affected_per_issue).map((n) => n.slug),
    },
    duplicate_groups: duplicate_groups.slice(0, 20),
    seed_imbalance: {
      cv: Math.round(cv * 100) / 100,
      severity: imbalanceSev,
      min_count: minCount,
      max_count: maxCount,
    },
    seeds_without_nodes: seedsWithoutNodes.map((s) => s.name),
    cross_seed_bridges: crossSeed,
    intra_seed_relations: intraSeed,
    degree_distribution,
    issues,
  };
}

export { PREFIX as L2_STRUCT_PREFIX };
